import { useEffect, useRef } from 'react';
import { jmrSquaredAttribution } from '../config/attribution';
import { demoPreview } from '../config/demo-preview';

/**
 * Consent context, visible at every scroll position.
 *
 * Sticky and above the pinned sections, so a reader never sees a stretch of the
 * page without knowing this is an unsolicited preview.
 */
export function DemoPreviewBanner() {
  const rootRef = useRef<HTMLDivElement>(null);

  // The banner overlays rather than displaces, so full-height sections can stay
  // a true 100dvh. Everything that has to clear it reads this measured height.
  useEffect(() => {
    const node = rootRef.current;
    if (!node) {
      return;
    }

    const publish = () => {
      document.documentElement.style.setProperty('--banner-h', `${node.offsetHeight}px`);
    };

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  // One line at every width. On a phone the banner sits above the hero, and the
  // four-line paragraph it used to be took 123px off the top of a 844px screen.
  // The full disclosure lives in the footer attribution; this line carries the
  // two facts a visitor needs first: this is not their site, and it comes down
  // when they ask.
  return (
    <div
      ref={rootRef}
      className="fixed inset-x-0 top-0 z-[70] border-b border-[color:var(--ink-600)] bg-[color:var(--ink-900)] px-[var(--gutter)] py-2 text-[color:var(--steel-400)]"
      role="status"
    >
      <div className="u-shell flex items-center justify-between gap-6">
        <p className="min-w-0 truncate text-[12px] leading-[1.45]">
          <span className="u-label hidden text-[11px] tracking-[0.08em] text-[color:var(--signal-500)] sm:inline">
            {demoPreview.badgeLabel}
            <span className="mx-2 text-[color:var(--ink-500)]">/</span>
          </span>
          <span className="lg:hidden">{demoPreview.bannerMessageShort}</span>
          <span className="hidden lg:inline">{demoPreview.bannerMessage}</span>
        </p>
        <a
          href={jmrSquaredAttribution.contactMailto}
          className="u-label hidden min-h-[44px] shrink-0 items-center border-b border-[color:var(--ink-500)] text-[11px] tracking-[0.08em] transition-colors hover:border-[color:var(--signal-500)] hover:text-[color:var(--bone-50)] sm:inline-flex"
          style={{ transitionDuration: 'var(--t-state)', transitionTimingFunction: 'var(--e-settle)' }}
        >
          {jmrSquaredAttribution.contactEmail}
        </a>
      </div>
    </div>
  );
}
